import * as React from 'react';
import {
  Box,
  GDSStyleObject,
  TextAction,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { Link, useLocation } from 'react-router-dom';

export const Table = ({ children }: React.PropsWithChildren) => (
  <Box
    as="table"
    css={{
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      textAlign: 'left',
      whiteSpace: 'nowrap',
      width: '100%',
    }}
  >
    {children}
  </Box>
);

interface TableCellProps extends React.PropsWithChildren {
  as?: 'td' | 'th';
  css?: GDSStyleObject;
  scope?: string;
  [key: string]: any;
}

export const BaseTableCell = ({
  as = 'td',
  scope,
  children,
  css,
}: TableCellProps) => (
  <Box as={as} scope={scope} css={css}>
    {children}
  </Box>
);

export const TableCell = ({ children, as, css, scope }: TableCellProps) => (
  <BaseTableCell
    as={as}
    css={{
      paddingX: '$half',
      paddingY: '$threeQuarters',
      verticalAlign: 'middle',
      ...css,
    }}
    scope={scope}
  >
    {children}
  </BaseTableCell>
);

interface TableCellWithLinkProps extends TableCellProps {
  to: string;
  tabIndex?: number;
}

export const TableCellWithLink = ({
  as,
  to,
  scope,
  tabIndex = 0,
  children,
  css,
}: TableCellWithLinkProps) => {
  const { state: locationState } = useLocation();
  return (
    <BaseTableCell as={as} scope={scope} css={css}>
      <TextAction
        as={Link}
        to={to}
        state={locationState}
        tabIndex={tabIndex}
        css={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          paddingX: '$half',
          paddingY: '$threeQuarters',
          '&:hover': {
            textDecoration: 'none',
          },
          '&&:active': {
            outline: 'none',
          },
        }}
      >
        {children}
      </TextAction>
    </BaseTableCell>
  );
};

export const LinkableCell = ({
  linkTo,
  children,
  ...rest
}: React.PropsWithChildren<TableCellProps & { linkTo?: string }>) => {
  if (linkTo) {
    return (
      <TableCellWithLink to={linkTo} tabIndex={-1} {...rest}>
        {children}
      </TableCellWithLink>
    );
  }
  return <TableCell {...rest}>{children}</TableCell>;
};

export const defaultTextCellCss: GDSStyleObject = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const TableHeading = ({
  children,
  css,
  ...rest
}: React.PropsWithChildren<{ css?: GDSStyleObject } & Record<string, any>>) => (
  <BaseTableCell
    as="th"
    scope="col"
    css={{
      paddingX: '$half',
      paddingY: '$threeQuarters',
      ...css,
    }}
    {...rest}
  >
    {children ? (
      <UtilityText emphasis="subtle" size="sm">
        {children}
      </UtilityText>
    ) : null}
  </BaseTableCell>
);

export interface TableRowProps {
  css?: GDSStyleObject;
}

export const TableRow = ({
  children,
  css,
}: React.PropsWithChildren<TableRowProps>) => (
  <Box
    as="tr"
    css={{
      borderTop: '1px solid $onSurfaceBorderSubdued',
      '&:hover': {
        backgroundColor: '$interactiveBackgroundHovered',
      },
      '&:active': {
        backgroundColor: '$interactiveBackgroundPressed',
      },
      ...css,
    }}
  >
    {children}
  </Box>
);
